import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { data, redirect, Form, useSearchParams } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import logo from '#app/assets/publicare-akademie-logo.png'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { ConfirmationEmailAccountEmail } from '#app/emails/confirmation-emailaccount-email.tsx'
import { SignupEmail } from '#app/emails/signup-email.tsx'
import { requireAnonymous } from '#app/utils/auth.server.ts'
import {
	ProviderConnectionForm,
	providerNames,
} from '#app/utils/connections.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema } from '#app/utils/user-validation.ts'
import { type Route } from './+types/registrieren.ts'
import { prepareVerification } from './verify.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const SignupSchema = z.object({
	email: EmailSchema,
})

export async function loader({ request }: Route.LoaderArgs) {
	await requireAnonymous(request)
	return null
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()

	await checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: SignupSchema.superRefine(async (data, ctx) => {
			const existingUser = await prisma.user.findUnique({
				where: { email: data.email, password: { isNot: null } },
				select: { id: true },
			})
			if (existingUser) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message: 'email_already_exists',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const existingUserWithoutPassword = await prisma.user.findUnique({
		where: { email: submission.value.email, password: null },
		select: { id: true, imported: true, email: true },
	})

	if (existingUserWithoutPassword) {
		const { verifyUrl, redirectTo, otp } = await prepareVerification({
			period: 10 * 60,
			request,
			type: existingUserWithoutPassword.imported
				? 'set-password-imported'
				: 'set-password',
			target: existingUserWithoutPassword.email,
		})

		const response = await sendEmail({
			to: existingUserWithoutPassword.email,
			subject: `Publicare Akademie - Bestätigung der E-Mail Adresse`,
			react: (
				<ConfirmationEmailAccountEmail
					onboardingUrl={verifyUrl.toString()}
					otp={otp}
				/>
			),
		})

		if (response.status === 'success') {
			return redirect(redirectTo.toString())
		}
	}

	const { email } = submission.value
	const { verifyUrl, redirectTo, otp } = await prepareVerification({
		period: 10 * 60,
		request,
		type: 'onboarding',
		target: email,
	})

	const response = await sendEmail({
		to: email,
		subject: `Anmeldung Publicare Akademie`,
		react: <SignupEmail onboardingUrl={verifyUrl.toString()} otp={otp} />,
	})

	if (response.status === 'success') {
		return redirect(redirectTo.toString())
	} else {
		return data(
			{
				result: submission.reply({ formErrors: [response.error.message] }),
			},
			{
				status: 500,
			},
		)
	}
}

export const meta: Route.MetaFunction = () => {
	return [{ title: 'Anmeldung | Publicare Akademie' }]
}

export default function SignupRoute({ actionData }: Route.ComponentProps) {
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getZodConstraint(SignupSchema),
		lastResult: actionData?.result,
		onValidate({ formData }) {
			console.log('onValidate: ', formData)
			const result = parseWithZod(formData, { schema: SignupSchema })
			return result
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex flex-col justify-center pt-20 pb-32">
			<div className="text-body-md text-muted-foreground flex flex-col items-center justify-center gap-8 text-center">
				<img
					src={logo}
					alt="Publicare Logo"
					className="mw-[400px]"
					style={{ height: 150 }}
				/>
				<h1 className="text-h2 font-alumni font-semibold">
					Willkommen bei Publicare Akademie!
				</h1>
				<p className="text-body-md text-muted-foreground mt-3">
					Bitte gib deine E-Mail Adresse ein....
				</p>
			</div>
			<div className="mx-auto mt-16 max-w-sm min-w-full sm:min-w-[368px]">
				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{
							htmlFor: fields.email.id,
							children: 'E-Mail',
						}}
						inputProps={{
							...getInputProps(fields.email, { type: 'email' }),
							autoFocus: true,
							autoComplete: 'email',
						}}
						errors={
							fields.email.errors &&
							fields.email.errors[0] &&
							fields.email.errors[0] === 'email_already_exists'
								? []
								: fields.email.errors
						}
					/>
					{fields.email.errors &&
						fields.email.errors[0] &&
						fields.email.errors[0] === 'email_already_exists' && (
							<div className="pb-4 text-red-500">
								Ein Benutzer mit dieser E-Mail Adresse existiert bereits. Wollen
								Sie sich{' '}
								<a className="text-pcteal-700 hover:underline" href={'/login'}>
									anmelden?
								</a>
							</div>
						)}
					<ErrorList errors={form.errors} id={form.errorId} />
					<StatusButton
						className="w-full"
						status={isPending ? 'pending' : (form.status ?? 'idle')}
						type="submit"
						disabled={isPending}
					>
						Absenden
					</StatusButton>
				</Form>
				<ul className="flex hidden flex-col gap-4 py-4">
					{providerNames.map((providerName) => (
						<>
							<hr />
							<li key={providerName}>
								<ProviderConnectionForm
									type="Signup"
									providerName={providerName}
									redirectTo={redirectTo}
								/>
							</li>
						</>
					))}
				</ul>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
